import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { ChargeListTable } from './ChargeListTable';
import { ChargeEntryForm } from './ChargeEntryForm';
import { useJobCharges } from '../hooks/useJobCharges';
import { useJobChargeTotal } from '../hooks/useJobChargeTotal';
import { useDeleteJobCharge } from '../hooks/useDeleteJobCharge';
import type { JobCharge } from '../types/jobCharge';

interface JobChargesTabProps {
  jobId: number;
}

export function JobChargesTab({ jobId }: JobChargesTabProps) {
  const [isForecast, setIsForecast] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCharge, setEditingCharge] = useState<JobCharge | null>(null);

  const {
    data: charges,
    isLoading: isLoadingCharges,
    error: chargesError,
    refetch: refetchCharges,
  } = useJobCharges(jobId, isForecast);

  const {
    data: total,
    isLoading: isLoadingTotal,
    error: totalError,
    refetch: refetchTotal,
  } = useJobChargeTotal(jobId, isForecast);

  const { deleteCharge, isDeleting } = useDeleteJobCharge();

  const handleAddCharge = () => {
    setEditingCharge(null);
    setIsFormOpen(true);
  };

  const handleEditCharge = (charge: JobCharge) => {
    setEditingCharge(charge);
    setIsFormOpen(true);
  };

  const handleDeleteCharge = async (chargeId: number) => {
    const success = await deleteCharge(jobId, chargeId);
    if (success) {
      refetchCharges();
      refetchTotal();
    }
  };

  const handleFormSuccess = () => {
    refetchCharges();
    refetchTotal();
  };

  const isLoading = isLoadingCharges || isLoadingTotal;
  const error = chargesError || totalError;

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <div className="p-4 space-y-4">
      {/* Header with toggle and add button */}
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          <Button
            variant={!isForecast ? 'default' : 'outline'}
            onClick={() => setIsForecast(false)}
            className={
              !isForecast
                ? 'bg-[#0A0A9E] text-white hover:bg-[#0A0A9E]/90'
                : 'text-gray-600 hover:bg-gray-100'
            }
          >
            Actual
          </Button>
          <Button
            variant={isForecast ? 'default' : 'outline'}
            onClick={() => setIsForecast(true)}
            className={
              isForecast
                ? 'bg-[#0A0A9E] text-white hover:bg-[#0A0A9E]/90'
                : 'text-gray-600 hover:bg-gray-100'
            }
          >
            Forecast
          </Button>
        </div>

        <Button variant="primary" onClick={handleAddCharge}>
          Add Charge
        </Button>
      </div>

      <ErrorAlert error={error} />

      {/* Loading state */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" label="Loading charges..." />
        </div>
      ) : (
        <>
          {/* Contract Total Display */}
          <div className="bg-white rounded-[20px] border border-[#d9d9d9] p-6">
            <div className="text-2xl font-bold">
              Contract Total: ${formatCurrency(total?.totalAmount ?? 0)}
            </div>
            <div className="text-sm text-muted-foreground">
              {total?.chargeCount ?? 0} charge(s)
            </div>
          </div>

          {/* Charges table */}
          <ChargeListTable
            jobId={jobId}
            charges={charges}
            onEdit={handleEditCharge}
            onDelete={handleDeleteCharge}
            isDeleting={isDeleting}
          />
        </>
      )}

      {/* Charge entry form dialog */}
      <ChargeEntryForm
        jobId={jobId}
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingCharge(null);
        }}
        onSuccess={handleFormSuccess}
        editingCharge={editingCharge}
        isForecast={isForecast}
      />
    </div>
  );
}
